import React, { forwardRef } from 'react';

/**
 * TextareaComponent — Shopify/shadcn-style reusable textarea for the entire admin panel.
 *
 * Props:
 *   id                 – HTML id
 *   name               – field name
 *   value              – controlled value
 *   onChange           – change handler
 *   placeholder        – placeholder text
 *   required           – shows a * next to the label
 *   className          – extra classes merged onto the textarea element
 *   containerClassName – extra classes merged onto the outer wrapper element
 *   label              – label text rendered above the textarea
 *   labelAction        – JSX element rendered on the right side of the label (e.g. AI generator button)
 *   tabIndex           – HTML tabIndex
 *   onKeyDown          – keyDown handler
 *   error              – validation error string shown below textarea (red)
 *   helpText           – helper text shown below textarea (gray)
 *   rows               – number of visible text lines (default: 3)
 *   cols               – width in average character widths
 *   maxLength          – maximum length of characters allowed
 *   showCharCount      – boolean to display character counter
 *   resize             – 'none' | 'vertical' | 'horizontal' | 'both' | boolean (default: 'vertical')
 *   size               – 'sm' | 'md' (default)
 *   disabled           – disables the textarea
 *   readOnly           – makes textarea read-only
 */
const TextareaComponent = forwardRef(({
  id,
  name,
  value,
  onChange,
  placeholder,
  required = false,
  className = '',
  containerClassName = '',
  label,
  labelAction,
  tabIndex,
  onKeyDown,
  error = '',
  helpText = '',
  rows = 3,
  cols,
  maxLength,
  showCharCount = false,
  resize = 'vertical',
  size = 'md',
  disabled = false,
  readOnly = false,
  ...props
}, ref) => {
  const hasValue = value !== undefined && value !== null && value.toString().trim() !== '';

  // ── Size Styles ───────────────────────────────────────────────────────
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-3 py-2 text-sm',
  };

  const appliedSize = sizeClasses[size] || sizeClasses.md;

  // ── Resize Styles ─────────────────────────────────────────────────────
  let resizeClass = 'resize-y';
  if (resize === false || resize === 'none') {
    resizeClass = 'resize-none';
  } else if (resize === 'horizontal') {
    resizeClass = 'resize-x';
  } else if (resize === 'both' || resize === true) {
    resizeClass = 'resize';
  } else if (typeof resize === 'string' && resize.startsWith('resize-')) {
    resizeClass = resize;
  }

  // ── Textarea classes ──────────────────────────────────────────────────
  const textareaClasses = [
    'w-full border rounded-lg outline-none transition-all duration-150',
    'text-[var(--primary-text-color)] placeholder-gray-400',
    'bg-[var(--secondary-bg-color)]',
    'border-[var(--border-color)]',
    'hover:border-[var(--focus-border-color)]',
    'focus:border-[var(--focus-border-color)] focus:ring-0 focus:bg-[var(--secondary-bg-color)]',
    error ? 'border-[var(--color-danger)] focus:ring-0 focus:border-[var(--color-danger)]' : '',
    disabled ? 'opacity-60 cursor-not-allowed bg-[var(--focus-tab-color)]' : '',
    hasValue && !disabled ? 'bg-[var(--secondary-bg-color)]' : '',
    appliedSize,
    resizeClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const currentLength = value ? String(value).length : 0;

  return (
    <div className={`w-full flex flex-col gap-1 text-left ${containerClassName}`.trim()}>
      {(label || labelAction) && (
        <div className='flex items-center justify-between mb-0.5'>
          {label ? (
            <label
              htmlFor={id}
              className='text-[var(--primary-text-color)] font-medium text-[13px] tracking-tight flex items-center mb-0'
              style={{ marginBottom: 0 }}
            >
              {label}
              {required && <span className='text-[var(--color-danger)] ml-0.5'>*</span>}
            </label>
          ) : <div />}
          {labelAction && <div>{labelAction}</div>}
        </div>
      )}

      <div className='relative w-full flex flex-col'>
        <textarea
          ref={ref}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
          cols={cols}
          maxLength={maxLength}
          className={textareaClasses}
          {...props}
        />
      </div>

      {(error || helpText || showCharCount) && (
        <div className='flex items-center justify-between text-xs mt-0.5'>
          <div>
            {error ? (
              <p className='text-[var(--color-danger)] font-medium m-0'>{error}</p>
            ) : (
              helpText && <p className='text-[var(--table-text-color)] m-0'>{helpText}</p>
            )}
          </div>

          {showCharCount && (
            <span className='text-[var(--table-text-color)] ml-auto text-[11px]'>
              {currentLength}{maxLength ? ` / ${maxLength}` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default TextareaComponent;
